import {
  AnalyticsSummarySchema,
  ApprovedHoursSchema,
  AreaSchema,
  DecisionTimeMetricsSchema,
  EmployeeSummarySchema,
  paginatedResponseSchema,
} from '@gestion-permisos/contracts';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useApi } from '../api/api-context.js';

/**
 * Filtros de la analítica en la URL, **como texto** (sin `Date` ni validaciones propias): la API los valida
 * y, por ejemplo, responde 400 si `desde` es posterior a `hasta`. Nombres en la URL → nombres de la API.
 */
export const FILTER_PARAMS = {
  desde: 'from',
  hasta: 'to',
  estado: 'status',
  q: 'q',
  tipo: 'permissionTypeId',
  area: 'areaId',
  empleado: 'employeeId',
} as const;

export type FilterName = keyof typeof FILTER_PARAMS | 'umbral';
export type AnalyticsFilters = Partial<Record<FilterName, string>>;

export function useAnalyticsFilters(): [AnalyticsFilters, (next: AnalyticsFilters) => void] {
  const [params, setParams] = useSearchParams();
  const filters: AnalyticsFilters = {};
  for (const name of [...Object.keys(FILTER_PARAMS), 'umbral'] as FilterName[]) {
    const value = params.get(name)?.trim();
    if (value) filters[name] = value;
  }
  const apply = (next: AnalyticsFilters) => {
    const updated = new URLSearchParams();
    for (const [name, value] of Object.entries(next)) if (value?.trim()) updated.set(name, value.trim());
    setParams(updated);
  };
  return [filters, apply];
}

/**
 * Query de la API con los filtros comunes. `thresholdHours` solo va en `decision-times` y solo si se indicó
 * (si no, la API usa 24): `summary`, `approved-hours` y la exportación no lo admiten.
 */
export function analyticsQuery(filters: AnalyticsFilters, options: { threshold?: boolean } = {}): string {
  const params = new URLSearchParams();
  for (const [name, apiName] of Object.entries(FILTER_PARAMS) as [keyof typeof FILTER_PARAMS, string][]) {
    const value = filters[name];
    if (value !== undefined) params.set(apiName, value);
  }
  if (options.threshold && filters.umbral !== undefined) params.set('thresholdHours', filters.umbral);
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function useAnalyticsSummary(filters: AnalyticsFilters) {
  const api = useApi();
  const query = analyticsQuery(filters);
  return useQuery({
    queryKey: ['analytics', 'summary', query],
    queryFn: () => api.get(`/api/v1/analytics/requests/summary${query}`, AnalyticsSummarySchema),
  });
}

const ApprovedHoursListSchema = z.array(ApprovedHoursSchema);

export function useApprovedHours(filters: AnalyticsFilters) {
  const api = useApi();
  const query = analyticsQuery(filters);
  return useQuery({
    queryKey: ['analytics', 'approved-hours', query],
    queryFn: () => api.get(`/api/v1/analytics/approved-hours${query}`, ApprovedHoursListSchema),
  });
}

export function useDecisionTimes(filters: AnalyticsFilters) {
  const api = useApi();
  const query = analyticsQuery(filters, { threshold: true });
  return useQuery({
    queryKey: ['analytics', 'decision-times', query],
    queryFn: () => api.get(`/api/v1/analytics/decision-times${query}`, DecisionTimeMetricsSchema),
  });
}

/** Áreas para el selector, **solo para el Administrador** (`enabled`): un Responsable no llama a `/areas`. */
export function useAreaOptions(enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['analytics', 'areas'],
    queryFn: () => api.get('/api/v1/areas?pageSize=100', paginatedResponseSchema(AreaSchema)),
    enabled,
  });
}

/** Personas del equipo para el selector, **solo para el Responsable** (`enabled`). */
export function useTeamOptions(enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['analytics', 'team'],
    queryFn: () => api.get('/api/v1/me/team?pageSize=100', paginatedResponseSchema(EmployeeSummarySchema)),
    enabled,
  });
}
